import { BCMSCurrency, BrandingStyle, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from 'components/custom-ui/DateSelect';
import { FormContainer, FormRowView } from 'components/shared';
import IconToggle from "components/shared/views/IconToggle";
import { UIAmountInput, UITextInput } from 'components/ui';
import React, { useEffect, useState } from 'react';
import { SVGAsset } from "res";
import TASPositivePayCheckForm from "../../classes/TASPositivePayCheckForm";

/**
 * @function TASPositivePayAddChecksMobileView
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay Add Check mobile content view.
 */
export default function TASPositivePayAddChecksMobileView(props) {
    const { data, ui, onChange, onDeleteClick, onMobileSave, onMobileCancel, onMobileEdit } = useStateManager();
    const checkForms = data.checkForms;
    const canDeleteCheckForms = checkForms.length > 1;
    const defaultEditCheckForms = checkForms.reduce((acc, item) => {
        const newItem = TASPositivePayCheckForm.init({ ...item.record });
        acc.push(newItem);
        return acc;
    }, []);
    const [ stateEditCheckForms, setStateEditCheckForms ] = useState([ ...defaultEditCheckForms ]);
    const { open, close } = useBCMSModal();

    // Reset the check form in the state only, not globally.
    const onModalYesClick = checkFormID => val => {
        var editCheckForms = [ ...stateEditCheckForms ];
        var index = editCheckForms.findIndex(item => item.id === checkFormID);
        var selectedCheckForm = editCheckForms[index];
        var checkForm = TASPositivePayCheckForm.init({ ...selectedCheckForm.record });
        checkForm = checkForm.reset();    
        editCheckForms.splice(index, 1, checkForm);
        setStateEditCheckForms([ ...editCheckForms ]);
    }

    const openModal = checkFormID => val => {
        open("TASPositivePayCancelAddCheckModal", { onYesClick: onModalYesClick(checkFormID) } );
    }
 
    const onMobileChange = (id, prop, setVal) => val => {
        const checkForms = [ ...stateEditCheckForms ];
        const findSelectedIndex = checkForms.findIndex(item => item.id === id);
        const selectedCheckform = checkForms.filter(item => item.id === id)[0];
        const filteredCheckForms = checkForms.filter(item => item.id !== id);
        const currCheckFormRecord = { ...selectedCheckform.record };

        if(setVal) {
            currCheckFormRecord[prop] = setVal;
        } else {
            currCheckFormRecord[prop] = val;
        }
        
        const updatedCheckForm = TASPositivePayCheckForm.init({ ...currCheckFormRecord, account: currCheckFormRecord.account.id });
        
        const updatedCheckForms = [ 
            ...filteredCheckForms.slice(0, findSelectedIndex), 
            updatedCheckForm, 
            ...filteredCheckForms.slice(findSelectedIndex) 
        ];
        
        setStateEditCheckForms(updatedCheckForms);
    };

    useEffect(() => {
        setStateEditCheckForms([ ...data.checkForms ]);
        
        if(props.cancelModal.isActive) {
            open("TASPositivePayCancelAddCheckModal", { onYesClick: onModalYesClick("0") } );
        }
    }, [ data.checkForms, props.cancelModal ]);

    const rows = stateEditCheckForms.reduce((acc, item, index) => {
        const account = item.account ?? {};
        const chevronIconClosedClass = item.mobileViewIsOpen ? "" : "closed";
        const amount = item.amount ? BCMSCurrency.init(item.amount).format() : undefined;
        const editAmount = item.amount ? BCMSCurrency.init(item.amount).format() : undefined;
        const editingAmount = item.amount ? item.amount.amount : undefined;

        acc.push(
            <div key={ item.id } className="tas-positive-pay-add-checks-mobile-view show-on-mobile">
                <div className="flex-container align-top">
                    <CustomAccordion isOpen={ item.mobileViewIsOpen }>
                        <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                                <div className="set-width">
                                    <CustomAccordionToggle className={ "search" } onClick={ onMobileChange(item.id, "mobileViewIsOpen") }>
                                        <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                                    </CustomAccordionToggle>
                                </div>
                                <div className="flex-width">
                                    <div className="flex-container flex-vertical check-details">
                                        <div className="check-detail-row">
                                            <span className="sub-text">Account: </span>
                                            <b>{ account.nickname } - { account.number }</b>
                                        </div>
                                        <div className="check-detail-row">
                                            <span className="sub-text">Issued Date: </span>
                                            <b>{ item.issuedDate.format("m/d/Y") }</b>
                                        </div>
                                        <div className="check-detail-row">
                                            <span className="sub-text">Check Number: </span>
                                            <b>{ item.checkNumber }</b>
                                        </div>
                                        <div className="check-detail-row">
                                            <span className="sub-text">Amount: </span>
                                            <b>{ amount }</b>
                                        </div>
                                    </div>
                                </div>
                        </div>
                        { item.mobileViewIsEditing && 
                        <CustomAccordionContent className="flex-container flex-vertical tas-positive-pay-add-check-page-accordion-content">
                            <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileChange(item.id, "mobileViewIsOpen") }>
                                <BrandingStyle className="flex-width sub-text" element={<p >EDIT CHECK</p>}/>
                                <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                            </CustomAccordionToggle>
                            <FormContainer className="small-form-width">
                                <FormRowView text="Issued Date">
                                    <DateSelect active={ item.issuedDate.isActive } enablePast={ true } value={ item.issuedDate } onChange={ onMobileChange(item.id, "issuedDate") } />
                                </FormRowView>
                                <FormRowView text="Check Number">
                                    <UITextInput value={ item.checkNumber } onChange={ onMobileChange(item.id, "checkNumber") } />
                                </FormRowView>
                                <FormRowView text="Amount">
                                    <UIAmountInput className="amount-field" value={ editingAmount } showClearButton={ false } onChange={ onMobileChange(item.id, "amount") } />
                                </FormRowView>
                                <FormRowView text="Payee">
                                    <UITextInput value={ item.payee } onChange={ onMobileChange(item.id, "payee") } />
                                </FormRowView>
                                <FormRowView text="Reference Number">
                                    <UITextInput value={ item.referenceNumber } onChange={ onMobileChange(item.id, "referenceNumber") } />
                                </FormRowView>
                                <FormRowView className="type-form-view" text="Type">
                                    <CustomSelect active={ item.type.isActive } value={ item.type } onChange={ onMobileChange(item.id, "type") }>
                                        <CustomSelectItem value="issue">Issue</CustomSelectItem>
                                        <CustomSelectItem value="stop">Stop</CustomSelectItem>
                                        <CustomSelectItem value="void">Void</CustomSelectItem>
                                    </CustomSelect>
                                </FormRowView>
                                <div className="flex-container flex-vertical">
                                    <a className="clickable save" onClick={ onMobileSave(item.id, stateEditCheckForms) }>Save</a>
                                    <a className="clickable cancel" onClick={ openModal(item.id) }>Cancel</a>
                                </div>
                            </FormContainer>
                        </CustomAccordionContent>
                        }
                        { !item.mobileViewIsEditing && 
                        <CustomAccordionContent className="flex-container flex-vertical">
                            <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileChange(item.id, "mobileViewIsOpen") }>
                                <BrandingStyle className="flex-width sub-text" element={<p >DETAILS</p>}/>
                                <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                            </CustomAccordionToggle>
                            <FormContainer className="small-form-width">
                                <FormRowView className="sub-text" text="Issued Date">
                                    <b>{ item.issuedDate.format("m/d/Y") }</b>
                                </FormRowView>
                                <FormRowView className="sub-text" text="Check Number">
                                    <b>{ item.checkNumber }</b>
                                </FormRowView>
                                <FormRowView className="sub-text" text="Amount">
                                    <b>{ editAmount }</b>
                                </FormRowView>
                                <FormRowView className="sub-text" text="Payee">
                                    <b>{ item.payee }</b>
                                </FormRowView>
                                <FormRowView className="sub-text" text="Reference Number">
                                    <b>{ item.referenceNumber }</b>
                                </FormRowView>
                                <FormRowView className="sub-text type type-form-view" text="Type">
                                    <b>{ item.type }</b>
                                </FormRowView>
                                <div className="flex-container flex-vertical">
                                    <a className="clickable edit-link" onClick={ onMobileChange(item.id, "mobileViewIsEditing", true) }>Edit</a>
                                    { canDeleteCheckForms && <a className="clickable" onClick={ onDeleteClick(item.id) }>Delete</a> }
                                </div>

                            </FormContainer>
                        </CustomAccordionContent>
                        }
                    </CustomAccordion>
                </div>
            </div>
        );
        return acc;
    }, [])
    
    return rows;
}